/**
 * Analytics calculations — client-side derived metrics from existing API data.
 */

export function calculateRampRate(ctlArray: number[]): number {
  if (ctlArray.length < 8) return 0;
  return ctlArray[ctlArray.length - 1] - ctlArray[ctlArray.length - 8];
}

export type RampRisk = 'low' | 'moderate' | 'high' | 'critical';
export function getRampRateRisk(rampRate: number): RampRisk {
  const abs = Math.abs(rampRate);
  if (abs <= 5) return 'low';
  if (abs <= 8) return 'moderate';
  if (abs <= 12) return 'high';
  return 'critical';
}

export interface ProjectionPoint {
  date: string; ctl: number; atl: number; tsb: number; projected: boolean;
}

export function projectPMC(
  currentCtl: number, currentAtl: number, startDate: Date,
  weeksAhead = 8, plannedDailyTss = 0,
): ProjectionPoint[] {
  const points: ProjectionPoint[] = [];
  let ctl = currentCtl, atl = currentAtl;
  const days = weeksAhead * 7;
  for (let i = 1; i <= days; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    ctl = ctl + (plannedDailyTss - ctl) / 42;
    atl = atl + (plannedDailyTss - atl) / 7;
    points.push({ date: date.toISOString().split('T')[0], ctl: Math.round(ctl * 10) / 10, atl: Math.round(atl * 10) / 10, tsb: Math.round((ctl - atl) * 10) / 10, projected: true });
  }
  return points;
}

export interface WeeklyTSSPoint { weekStart: string; tss: number; avgDaily: number; }

export function aggregateWeeklyTSS(dates: string[], dailyTss: number[]): WeeklyTSSPoint[] {
  if (!dates.length) return [];

  const parseIsoDate = (iso: string): Date | null => {
    const parts = iso.split('-').map(Number);
    if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return null;
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  };

  const weekMap = new Map<string, number[]>();
  dates.forEach((date, i) => {
    const d = parseIsoDate(date);
    if (!d) return;
    const day = d.getUTCDay();
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() - ((day + 6) % 7));
    const key = monday.toISOString().slice(0, 10);
    if (!weekMap.has(key)) weekMap.set(key, []);
    const tss = Number.isFinite(dailyTss[i]) ? (dailyTss[i] as number) : 0;
    weekMap.get(key)!.push(tss);
  });
  return Array.from(weekMap.entries()).sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, values]) => ({
      weekStart, tss: Math.round(values.reduce((s, v) => s + v, 0)),
      avgDaily: Math.round(values.reduce((s, v) => s + v, 0) / values.length),
    }));
}

export interface HeatmapDay { date: string; value: number; weekday: number; week: number; }

export function buildAnnualHeatmap(dates: string[], values: number[]): HeatmapDay[] {
  const map = new Map<string, number>();
  dates.forEach((d, i) => {
    const value = Number.isFinite(values[i]) ? (values[i] as number) : 0;
    map.set(d, value);
  });
  const today = new Date();
  const start = new Date(today);
  start.setFullYear(start.getFullYear() - 1);
  const days: HeatmapDay[] = [];
  let week = 0, lastWeekday = -1;
  for (let d = new Date(start); d <= today; d.setDate(d.getDate() + 1)) {
    const dateStr = d.toISOString().split('T')[0];
    const weekday = (d.getDay() + 6) % 7;
    if (weekday <= lastWeekday) week++;
    lastWeekday = weekday;
    days.push({ date: dateStr, value: map.get(dateStr) ?? 0, weekday, week });
  }
  return days;
}

export interface PowerDurationPoint { duration: number; durationLabel: string; power: number; }

export function buildPowerDurationCurve(peaks: Array<{ duration_seconds: number; power: number }>): PowerDurationPoint[] {
  return peaks.sort((a, b) => a.duration_seconds - b.duration_seconds).map((p) => ({
    duration: p.duration_seconds,
    durationLabel: p.duration_seconds < 60 ? `${p.duration_seconds}s` : p.duration_seconds < 3600 ? `${Math.floor(p.duration_seconds / 60)}m` : `${Math.floor(p.duration_seconds / 3600)}h`,
    power: Math.round(p.power),
  }));
}

export interface ComplianceScore { overallPct: number; sessionsPct: number; loadPct: number; minutesPct: number; }

export function calculateCompliance(
  planned: { count: number; load: number; minutes: number },
  completed: { count: number; load: number; minutes: number },
): ComplianceScore {
  const safe = (a: number, b: number) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);
  const sessionsPct = safe(completed.count, planned.count);
  const loadPct = safe(completed.load, planned.load);
  const minutesPct = safe(completed.minutes, planned.minutes);
  return { overallPct: Math.round((sessionsPct + loadPct + minutesPct) / 3), sessionsPct, loadPct, minutesPct };
}
